import MfaCodeField from '@/components/Mfa/MfaCodeField';
import {
  StartTotpEnrollmentMutation,
  useConfirmTotpEnrollmentMutation,
  useStartTotpEnrollmentMutation,
} from '@/generated/graphql';
import { useCopyToClipboard } from '@/hooks/clipboard';
import { useLayoutStyles } from '@/hooks/styles';
import { graphQLErrorCode } from '@/utils/apollo-errors';
import {
  formatTotpSecret,
  isCompleteCode,
  totpSecretFromUrl,
} from '@/utils/mfa';
import { MFA_ERROR_CODES, mfaErrorMessage } from '@/utils/mfa-errors';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { Alert, Skeleton } from '@material-ui/lab';
import { QRCodeSVG } from 'qrcode.react';
import React, { useEffect, useRef, useState } from 'react';

const QR_SIZE = 176;

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'auto',
  },
  step: {
    '& + &': {
      marginTop: theme.spacing(3),
    },
  },
  qr: {
    display: 'flex',
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    // A QR code needs a light background to scan, in either theme.
    backgroundColor: '#fff',
    border: `1px solid ${theme.palette.divider}`,
  },
  secretBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    padding: theme.spacing(1, 1, 1, 2),
    minHeight: 48,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
  },
  secret: {
    flex: 1,
    fontFamily: 'monospace',
    fontSize: '0.9375rem',
    letterSpacing: '0.05em',
    // Wrap between groups of 4, never inside one.
    wordSpacing: '0.25em',
  },
}));

type Enrollment = StartTotpEnrollmentMutation['startTotpEnrollment'];

interface TotpEnrollmentDialogProps {
  open: boolean;
  onClose: () => void;
  /** Enrollment is confirmed; show these recovery codes once. */
  onEnrolled: (recoveryCodes: string[]) => void;
  /**
   * The enroll-only sign-in ended (auth:mfa_challenge_invalid), so the
   * password must be entered again. Without it the dialog shows the error.
   */
  onSignInExpired?: (message: string) => void;
}

export default function TotpEnrollmentDialog({
  open,
  onClose,
  onEnrolled,
  onSignInExpired,
}: TotpEnrollmentDialogProps): JSX.Element {
  const classes = useStyles();
  const layoutClasses = useLayoutStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const copySecret = useCopyToClipboard(
    'Setup key copied',
    'Could not copy. Select the key and copy it.',
  );
  const [startTotpEnrollment, { loading: starting }] =
    useStartTotpEnrollmentMutation();
  const [confirmTotpEnrollment, { loading: confirming }] =
    useConfirmTotpEnrollmentMutation();
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [code, setCode] = useState('');
  // The last failed call; its code picks the message and the actions.
  const [error, setError] = useState<unknown>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const secret = enrollment ? totpSecretFromUrl(enrollment.otpauthUrl) : '';
  const busy = starting || confirming;
  const expired = graphQLErrorCode(error) === MFA_ERROR_CODES.enrollmentInvalid;

  function fail(err: unknown) {
    if (
      onSignInExpired &&
      graphQLErrorCode(err) === MFA_ERROR_CODES.challengeInvalid
    ) {
      onSignInExpired(mfaErrorMessage(err));
      return;
    }

    setError(err);
  }

  async function start() {
    setEnrollment(null);
    setCode('');
    setError(null);

    try {
      const { data } = await startTotpEnrollment();
      setEnrollment(data?.startTotpEnrollment ?? null);
    } catch (err) {
      fail(err);
    }
  }

  // Focus the code field once the QR code is there: scan, then type.
  useEffect(() => {
    if (enrollment) {
      inputRef.current?.focus();
    }
  }, [enrollment]);

  useEffect(() => {
    if (open) {
      start();
    }
    // Start a new enrollment each time the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function confirm(value: string) {
    if (!enrollment || busy) {
      return;
    }

    setError(null);

    try {
      const { data } = await confirmTotpEnrollment({
        variables: {
          input: { enrollmentId: enrollment.enrollmentId, code: value },
        },
        // The card behind the codes dialog catches up in the background.
        refetchQueries: ['ViewerMfa'],
      });
      onEnrolled(data?.confirmTotpEnrollment.recoveryCodes ?? []);
    } catch (err) {
      fail(err);
      setCode('');
      inputRef.current?.focus();
    }
  }

  function handleCodeChange(value: string) {
    setCode(value);

    if (isCompleteCode('totp', value)) {
      confirm(value);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      aria-labelledby="totp-enrollment-dialog-title"
      fullScreen={fullScreen}
      maxWidth="xs"
      fullWidth
    >
      <div className={layoutClasses.progress}>{busy && <LinearProgress />}</div>
      <DialogTitle id="totp-enrollment-dialog-title">
        Set up an authenticator app
      </DialogTitle>
      <form
        className={classes.form}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          confirm(code);
        }}
      >
        <DialogContent dividers>
          {Boolean(error) && (
            <Box mb={2}>
              <Alert
                severity="error"
                variant="outlined"
                role="alert"
                action={
                  expired ? (
                    <Button color="inherit" size="small" onClick={start}>
                      Start again
                    </Button>
                  ) : undefined
                }
              >
                {mfaErrorMessage(error)}
              </Alert>
            </Box>
          )}

          <section className={classes.step}>
            <Typography variant="subtitle2" gutterBottom>
              1. Scan this QR code
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Use an authenticator app such as Google Authenticator, Microsoft
              Authenticator, 1Password or Authy.
            </Typography>
            <Box display="flex" justifyContent="center" my={2}>
              {enrollment ? (
                <div className={classes.qr}>
                  <QRCodeSVG
                    value={enrollment.otpauthUrl}
                    size={QR_SIZE}
                    role="img"
                    aria-label="QR code for your authenticator app"
                  />
                </div>
              ) : (
                <Skeleton
                  variant="rect"
                  width={QR_SIZE + theme.spacing(2)}
                  height={QR_SIZE + theme.spacing(2)}
                />
              )}
            </Box>
            {fullScreen && enrollment && (
              <Box display="flex" justifyContent="center" mb={2}>
                <Button
                  variant="outlined"
                  color="primary"
                  href={enrollment.otpauthUrl}
                  startIcon={<OpenInNewIcon />}
                >
                  Open in authenticator app
                </Button>
              </Box>
            )}
            <Typography variant="body2" color="textSecondary">
              Can’t scan it? Enter this setup key in the app:
            </Typography>
            <div className={classes.secretBox}>
              {secret ? (
                <>
                  <Typography
                    className={classes.secret}
                    data-testid="totp-secret"
                  >
                    {formatTotpSecret(secret)}
                  </Typography>
                  <Tooltip title="Copy setup key">
                    <IconButton
                      size="small"
                      aria-label="Copy setup key"
                      onClick={() => copySecret(secret)}
                    >
                      <FileCopyOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <Skeleton width="80%" />
              )}
            </div>
          </section>

          <section className={classes.step}>
            <Typography variant="subtitle2" gutterBottom>
              2. Enter the 6-digit code from the app
            </Typography>
            <Box mt={1}>
              <MfaCodeField
                kind="totp"
                value={code}
                onChange={handleCodeChange}
                inputRef={inputRef}
                disabled={!enrollment}
              />
            </Box>
          </section>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy} size="large">
            Cancel
          </Button>
          <Button
            color="primary"
            size="large"
            type="submit"
            disabled={busy || !enrollment || !isCompleteCode('totp', code)}
          >
            Turn on
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
